import React from 'react';
import type { TimelineItemType } from '../../../types/timeline';
import { TIMELINE_COLORS } from '../../../constants/timeline';

interface TimelineLegendProps {
  activeTypes: TimelineItemType[];
  onToggleType: (type: TimelineItemType) => void;
}

const TimelineLegend: React.FC<TimelineLegendProps> = ({
  activeTypes,
  onToggleType
}) => {
  // All available item types
  const TIMELINE_TYPES: TimelineItemType[] = [
    'note', 
    'careerCard', 
    'resume', 
    'review', 
    'workExperience', 
    'file', 
    'retrospective'
  ];

  return (
    <div 
      className="flex flex-wrap justify-center items-center gap-2"
      role="group"
      aria-label="Timeline filters"
    >
      {TIMELINE_TYPES.map((type) => (
        <LegendItem
          key={type}
          type={type}
          isActive={activeTypes.includes(type)}
          onToggle={() => onToggleType(type)}
        />
      ))}
    </div>
  );
};

// Separate LegendItem component for better reusability
interface LegendItemProps {
  type: TimelineItemType;
  isActive: boolean;
  onToggle: () => void;
}

const LegendItem: React.FC<LegendItemProps> = ({ 
  type,
  isActive,
  onToggle
}) => {
  // Format the type name for display (e.g., "workExperience" -> "Work Experience")
  const formatTypeName = (type: string) => {
    return type
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  // Create a string value for the aria-checked attribute
  const ariaChecked = isActive ? "true" : "false";

  return (
    <div
      className={`flex items-center cursor-pointer px-3 py-1 rounded-full transition-colors ${
        isActive 
          ? 'bg-opacity-90' 
          : 'opacity-50 hover:opacity-70'
      }`}
      onClick={onToggle}
      role="checkbox"
      aria-checked={isActive}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{ 
        backgroundColor: isActive ? TIMELINE_COLORS[type] : 'rgba(255,255,255,0.1)'
      }}
    >
      <div
        className={`w-3 h-3 rounded-full mr-2 ${isActive ? '' : 'opacity-50'}`}
        style={{ backgroundColor: isActive ? '#FFFFFF' : TIMELINE_COLORS[type] }}
        aria-hidden="true"
      ></div>
      <span className={`text-xs ${isActive ? 'text-white font-medium' : 'text-gray-300'}`}>
        {formatTypeName(type)}
      </span>
    </div>
  );
};

export default TimelineLegend; 